<template>
  <el-header>
    <div class="title">
      <img :src="logo_url" alt="" />
      <h3>Home</h3>
    </div>

    <el-alert
      class="confirmation-alert"
      v-if="reservationCreatedFromBooking.show"
      :title="`Reservation Confirmation #${reservationCreatedFromBooking.confirmation_no} created`"
      type="success"
      show-icon
    />

    <div class="header-right">
      <language-selector />

      <div ref="userMenuTrigger" class="name" @click="show = !show" @mouseleave="show = false"
         @keydown.tab="handleTriggerTab"
         v-a11y-keyboard="{
          enter: () => show = !show,
          space: () => show = !show,
          escape: closeAndFocusTrigger,
          arrowDown: openAndFocusDropdown,
          arrowUp: openAndFocusLastItem
        }"
        tabindex="0" role="button"
        aria-haspopup="menu"
        :aria-expanded="show"
        aria-controls="user-menu-dropdown"
        :aria-label="`${first_name} ${last_name}, user menu`">
      <div class="user-name">
        <vue-feather type="user"></vue-feather>
        <span>{{ `${first_name} ${last_name}` }}</span>
        <vue-feather type="chevron-down" v-if="!show"></vue-feather>
        <vue-feather type="chevron-up" v-else></vue-feather>
      </div>

      <div
        class="logout"
        v-if="show"
        id="user-menu-dropdown"
        role="menu"
        @keydown.down.prevent.stop="navigateMenu(1)"
        @keydown.up.prevent.stop="navigateMenu(-1)"
        @keydown.home.prevent.stop="navigateMenu('first')"
        @keydown.end.prevent.stop="navigateMenu('last')"
        @keydown.tab.prevent="handleMenuTab"
      >
        <div class="dropdown-user-login">
           <ExternalLinks :showExternalUserDetails="true" :path="$route.path"/>
           <div @click="logout"
                v-a11y-keyboard="{
                  enter: logout,
                  space: logout,
                  escape: closeAndFocusTrigger
                }"
                tabindex="0"
                role="menuitem"
                :aria-label="$t('btn_logout')"
                class="logout-content"
                ref="logoutBtn">
        <vue-feather type="log-out"></vue-feather>
        <span>{{ $t('btn_logout') }}</span>
        </div>
                </div>

      </div>
    </div>
    </div><!-- /.header-right -->

    <el-dropdown trigger="click" class="user-icon-mobile">
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item disabled>{{ `${first_name} ${last_name}` }}</el-dropdown-item>
          <el-dropdown-item divided @click="logout">
            <el-icon class="dropdown-icon" :size="20">
              <ArrowLeft />
            </el-icon>
            {{ $t('btn_logout') }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
      <span class="el-dropdown-link">
        <vue-feather type="user"></vue-feather>
        <el-icon :size="20">
          <ArrowDown />
        </el-icon>
      </span>
    </el-dropdown>
  </el-header>
</template>

<script>
import { mapState } from 'vuex'
import 'vue-feather'
import 
  ExternalLinks
 from "@hotelkeyrepo/hk-trainkey-vue-3/src/packages/external-links/src/ExternalLinks.vue";
import LanguageSelector from '@/components/LanguageSelector.vue'

export default {
  name: 'HkHeader',
  components: {
    ExternalLinks,
    LanguageSelector
  },
  mounted() {
    this.activate()
  },
  watch: {
    show(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$nextTick(() => {
          this.enhanceExternalItems()
        })
      }
    }
  },
  data() {
    return {
      logo_url: window.hkConfigEnvs.VUE_APP_LOGO,
      googleSignInParams: {
        client_id: window.hkConfigEnvs.VUE_APP_GOOGLE_CLIENT_ID,
        hosted_domain: 'hotelkeyapp.com'
      },
      reservationCreatedFromBooking: {
        confirmation_no: null,
        show: false
      },
      show: false
    }
  },
  computed: {
    ...mapState({
      user: (state) => state?.current?.user,
      hk_info: (state) => state.current?.hk_info,
      google_login: (state) => state.current?.google_login
    }),
    first_name() {
      return this.user?.first_name || ''
    },
    last_name() {
      return this.user?.last_name || ''
    }
  },
  methods: {
    logout() {
      this.$emit('logUserOut')
    },
    closeAndFocusTrigger() {
      this.show = false
      this.$nextTick(() => {
        this.$refs.userMenuTrigger?.focus()
      })
    },
    openAndFocusDropdown() {
      this.show = true
      this.$nextTick(() => {
        this.enhanceExternalItems()
        const items = this.getMenuItems()
        items[0]?.focus()
      })
    },
    openAndFocusLastItem() {
      this.show = true
      this.$nextTick(() => {
        this.enhanceExternalItems()
        const items = this.getMenuItems()
        items[items.length - 1]?.focus()
      })
    },
    getMenuItems() {
      const dropdown = document.getElementById('user-menu-dropdown')
      if (!dropdown) return []
      return Array.from(dropdown.querySelectorAll('[role="menuitem"]'))
    },
    enhanceExternalItems() {
      const dropdown = document.getElementById('user-menu-dropdown')
      if (!dropdown) return
      const externalItems = dropdown.querySelectorAll(
        '.user-details-external:not([data-a11y-enhanced])'
      )
      externalItems.forEach((el) => {
        el.setAttribute('tabindex', '0')
        el.setAttribute('role', 'menuitem')
        el.setAttribute('data-a11y-enhanced', '1')
        const text = el.textContent.trim()
        if (text) el.setAttribute('aria-label', text)
        el.addEventListener('keydown', this.handleExternalItemKeydown)
      })
    },
    handleExternalItemKeydown(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.currentTarget.click()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        this.closeAndFocusTrigger()
      }
    },
    navigateMenu(direction) {
      const items = this.getMenuItems()
      if (items.length === 0) return
      const currentIdx = items.indexOf(document.activeElement)
      let nextIdx
      if (direction === 'first') nextIdx = 0
      else if (direction === 'last') nextIdx = items.length - 1
      else if (direction === 1) nextIdx = (currentIdx + 1) % items.length
      else nextIdx = currentIdx <= 0 ? items.length - 1 : currentIdx - 1
      items[nextIdx]?.focus()
    },
    handleTriggerTab(e) {
      if (!this.show) return
      e.preventDefault()
      this.handleMenuTab(e)
    },
    handleMenuTab(e) {
      this.show = false
      this.$nextTick(() => {
        const trigger = this.$refs.userMenuTrigger
        const focusable = Array.from(
          document.querySelectorAll(
            'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.disabled && el.offsetParent !== null)
        const idx = focusable.indexOf(trigger)
        const targetIdx = e.shiftKey ? idx - 1 : idx + 1
        const target = focusable[targetIdx]
        ;(target || trigger)?.focus()
      })
    },
    activate() {
      if (!this.user) {
        this.logout()
      }

      if (this.$route.query.conf_no) {
        this.reservationCreatedFromBooking.confirmation_no = this.$route.query.conf_no
        this.reservationCreatedFromBooking.show = true
        setTimeout(() => {
          this.reservationCreatedFromBooking.show = false
          this.$router.replace({})
        }, 4000)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$blue: #46a0fc;
$dropdown-offset: 4px;

.el-header {
  padding: 10px 20px !important;
  display: flex;
  justify-content: space-between;
  border-bottom: 0.5px solid #dadada;

  .confirmation-alert {
    position: absolute;
    width: 37%;
    left: 30%;
    top: 12px;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title {
    display: flex;
    align-items: center;

    img {
      max-width: 35px;
      height: 35px;
      margin-right: 10px;
    }
  }

  .name {
    display: block;
    position: relative;
    cursor: pointer;
    min-width: 50px;
    user-select: none;

    .user-name {
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 5px;
      background-color: #fff;
      color: #000;

      .user-icon {
        margin-right: 5px;
      }

      span {
        text-transform: capitalize;
      }

      .caret-icon {
        margin-left: 5px;
      }
    }

    .logout {
      position: absolute;
      padding: 5px;
      border: 1px solid #e6e6e6;
      border-radius: 4px;
      top: calc(100% + #{$dropdown-offset});
      left: 0;
      right: 0;
      background-color: #fff;
      z-index: 99;
      display: flex;
      flex-direction: column;
      align-items: center;

      // Transparent bridge across the visual gap between the trigger and the
      // dropdown, so moving the cursor down never leaves .name and fires
      // its mouseleave (which would close the dropdown mid-move).
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        height: $dropdown-offset;
      }

      span {
        margin-left: 5px;
      }
    }

    .logout .logout-content :hover {
      background-color: $blue;
      color: white;
    }
  }
}
.user-icon-mobile {
  display: none;
}
.dropdown-user-login {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.logout-content {
  display: flex;
  flex-direction: row;
  align-items: center;
      width: 100%;
    padding: 0px 10px
}
@media (max-width: 768px) and (min-width: 420px) {
  .el-header {
    .confirmation-alert {
      position: absolute;
      width: 45% !important;
      left: 25% !important;
      top: 12px;
    }
  }
}

@media (max-width: 420px) {
  .el-header {
    .name {
      display: none;
    }
    .confirmation-alert {
      position: absolute;
      width: 90%;
      top: 70px;
      left: 5%;
    }

    .title {
      .img {
        height: 15px;
      }
    }
    .user-icon-mobile {
      display: block;
      color: black;
      font-size: 20px;
      z-index: 9;
    }
  }
}
</style>
